import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import LoadingIndicator from '../common/LoadingIndicator';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Scrollbar hiding styles
const scrollbarStyles = `
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

function EnrolledCourses({ enrolledCourses }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courseDetails, setCourseDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const [visibleCards, setVisibleCards] = useState(4);
  const [showRetakeModal, setShowRetakeModal] = useState(false);
  const [retakeLoading, setRetakeLoading] = useState(false);
  const [retakeCourse, setRetakeCourse] = useState(null);

  useEffect(() => {
    const fetchCourseDetails = async () => {
      if (!enrolledCourses || !Array.isArray(enrolledCourses)) {
        setLoading(false);
        return;
      }

      try {
        // Fetch all exam results once to detect failures per course
        const resultsSnap = await getDocs(collection(db, 'users', user.uid, 'examResults'));
        const failedByCourseId = {};
        resultsSnap.forEach((r) => {
          const d = r.data();
          if (!d || !d.courseId) return;
          const attempts = Number(d.attempts || 0);
          const score = Number(d.score || 0);
          if (attempts >= 3 && score < 75) {
            failedByCourseId[d.courseId] = true;
          }
        });

        const detailedCourses = await Promise.all(
          enrolledCourses.map(async (course) => {
            const courseDoc = await getDoc(doc(db, 'courses', course.courseId));
            if (!courseDoc.exists()) return null;
            const courseData = courseDoc.data();

            const progressDoc = await getDoc(
              doc(db, 'users', user.uid, 'courseProgress', course.courseId)
            );
            const progress = progressDoc.exists() ? progressDoc.data().progress : 0;

            const hasFailed = !!failedByCourseId[course.courseId];

            return {
              id: course.courseId,
              ...courseData,
              progress,
              hasFailed,
              isArchived: progress === 100 && !hasFailed
            };
          })
        );

        // Sort courses: completed courses last
        const sortedCourses = detailedCourses
          .filter(Boolean)
          .sort((a, b) => {
            // Failed courses first, then in-progress, then completed last
            const rank = (c) => (c.hasFailed ? 0 : (c.isArchived ? 2 : 1));
            return rank(a) - rank(b);
          });

        setCourseDetails(sortedCourses);
      } catch (error) {
        console.error('Error fetching course details:', error);
      }
      setLoading(false);
    };

    if (enrolledCourses.length === 0) {
      setLoading(false);
    } else {
      fetchCourseDetails();
    }
  }, [enrolledCourses, user.uid]);

  const handleResize = useCallback(() => {
    if (window.innerWidth < 480) setVisibleCards(1);
    else if (window.innerWidth < 768) setVisibleCards(1.5);
    else if (window.innerWidth < 1024) setVisibleCards(2);
    else if (window.innerWidth < 1440) setVisibleCards(3);
    else setVisibleCards(4);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const handleCourseClick = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleRetake = async () => {
    if (!retakeCourse) return;
    try {
      setRetakeLoading(true);
      // Reset progress
      await setDoc(doc(db, 'users', user.uid, 'courseProgress', retakeCourse.id), {
        progress: 0,
        enrolledAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      }, { merge: true });

      // Clear completed subcourses
      await setDoc(doc(db, 'users', user.uid, 'completedSubCourses', retakeCourse.id), {
        completed: {},
        firstCompletedAt: new Date().toISOString()
      }, { merge: true });

      // Delete prior exam results for this course
      const resultsSnap = await getDocs(collection(db, 'users', user.uid, 'examResults'));
      const deletions = [];
      resultsSnap.forEach((r) => {
        const d = r.data();
        if (d && d.courseId === retakeCourse.id) {
          deletions.push(deleteDoc(doc(db, 'users', user.uid, 'examResults', r.id)));
        }
      });
      await Promise.all(deletions);

      setShowRetakeModal(false);
      setRetakeCourse(null);
      navigate(`/courses/${retakeCourse.id}`);
    } catch (e) {
      console.error('Error resetting course for retake:', e);
      alert('Failed to reset course for retake. Please try again.');
    } finally {
      setRetakeLoading(false);
    }
  };

  const scroll = (direction) => {
    const container = containerRef.current;
    if (!container) return;
    const scrollAmount = container.offsetWidth;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  if (loading) return <LoadingIndicator />;

  if (courseDetails.length === 0) {
    return (
      <section id="enrolled-courses" className="bg-gradient-to-b from-white to-gray-50 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Enrolled Courses</h2>
          <p className="text-gray-600 text-sm md:text-base">No courses enrolled yet. Start your learning journey today!</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <style>{scrollbarStyles}</style>
      <section className="bg-gradient-to-b from-white to-gray-50 py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Your Learning Journey
            </h2>
            <p className="text-gray-600 text-sm md:text-base max-w-2xl">
              Track your progress and continue learning
            </p>
          </div>

          {/* Courses Container */}
          <div className="relative">
            {/* Left Navigation Button */}
            <button
              onClick={() => scroll('left')}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-iof hover:text-white text-iof rounded-full p-2 shadow-lg transition-all duration-300 -ml-6"
              aria-label="Scroll left"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>

            {/* Right Navigation Button */}
            <button
              onClick={() => scroll('right')}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-iof hover:text-white text-iof rounded-full p-2 shadow-lg transition-all duration-300 -mr-6"
              aria-label="Scroll right"
            >
              <ChevronRightIcon className="h-6 w-6" />
            </button>

            {/* Cards Container */}
            <div 
              ref={containerRef}
              className="overflow-x-auto flex gap-4 px-2 md:px-0 py-2 scroll-smooth hide-scrollbar"
              style={{
                WebkitOverflowScrolling: 'touch',
                scrollBehavior: 'smooth',
              }}
            >
              {courseDetails.map((course) => (
                <div
                  key={course.id}
                  className="flex-none w-[280px] md:w-[300px] transition-all duration-300 hover:scale-105"
                  onClick={() => handleCourseClick(course.id)}
                  style={{ contain: 'layout style paint' }}
                >
                  <div className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden h-full flex flex-col ${
                    course.isArchived ? 'bg-gray-50' : ''
                  }`}>
                    {/* Image Container */}
                    <div className="relative overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
                      <img 
                        src={course.image || "/images/rcourses.png"}
                        alt={course.title}
                        className={`w-full h-44 object-cover transition-transform duration-300 hover:scale-110 ${
                          course.isArchived ? 'opacity-75' : ''
                        }`}
                        loading="lazy"
                      />
                      {/* Progress Badge */}
                      <div className="absolute top-3 right-3 bg-iof/90 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg">
                        {course.hasFailed ? 'Failed' : `${Math.round(course.progress)}%`}
                      </div>
                      {/* Status Badge */}
                      <div className={`absolute top-3 left-3 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg ${
                        course.hasFailed 
                          ? 'bg-red-100 text-red-700' 
                          : course.isArchived 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {course.hasFailed ? 'Needs Retake' : (course.isArchived ? 'Completed' : 'In Progress')}
                      </div>
                    </div>
                    
                    {/* Content Container */}
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className={`font-bold text-base md:text-lg text-gray-900 mb-1 line-clamp-2 leading-tight ${
                        course.isArchived ? 'text-gray-600' : ''
                      }`}>
                        {course.title}
                      </h3>
                      
                      <p className={`text-sm mb-3 line-clamp-2 flex-1 leading-relaxed ${
                        course.isArchived ? 'text-gray-500' : 'text-gray-600'
                      }`}>
                        {course.description}
                      </p>

                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              course.hasFailed ? 'bg-red-500' : course.isArchived ? 'bg-green-500' : 'bg-iof'
                            }`}
                            style={{ width: `${course.progress}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Footer */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                          course.isArchived ? 'text-gray-400 bg-gray-100' : 'text-gray-500 bg-gray-100'
                        }`}>
                          {course.duration || "Self-paced"}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (course.hasFailed) {
                              setRetakeCourse(course);
                              setShowRetakeModal(true);
                            } else {
                              handleCourseClick(course.id);
                            }
                          }}
                          className={`text-sm font-semibold transition-colors duration-200 flex items-center gap-1 ${
                            course.hasFailed 
                              ? 'text-red-600 hover:text-red-700' 
                              : course.isArchived 
                              ? 'text-gray-500' 
                              : 'text-iof hover:text-iof/80'
                          }`}
                        >
                          {course.hasFailed ? 'Retake' : (course.isArchived ? 'Review' : 'Continue')}
                          {!course.isArchived && <span className="text-lg">→</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {showRetakeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 shadow-xl w-full max-w-md">
            <h3 className="text-xl font-semibold mb-2">Confirm Retake</h3>
            <p className="text-gray-700 mb-4">Retaking will reset your progress and attempts for "{retakeCourse?.title}". Continue?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRetakeModal(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleRetake}
                disabled={retakeLoading}
                className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)] disabled:opacity-50"
              >
                {retakeLoading ? 'Resetting...' : 'Confirm Retake'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EnrolledCourses;